// ======================================================================
// 📁 RoutingOperationsTable.jsx
// 📝 Purpose:
//   - Manage routing operations in Draft mode
//   - Process dropdown sourced from Process Master
//   - Machine Group assignment per operation
//   - Auto-fill setup/run/scrap from process
//   - Predecessor selection from earlier operations only
//   - CONTROLLED by parent (RoutingCreateOverlay)
// ======================================================================

import React, { useEffect, useState } from "react";
import axiosClient from "../../../../../api/axiosClient";

export default function RoutingOperationsTable({
  value = [],
  onChange,
  readOnly = false,
}) {
  const [operations, setOperations] = useState(value);
  const [processes, setProcesses] = useState([]);
  const [machineGroups, setMachineGroups] = useState([]);

  // ------------------------------------------------------------
  // Sync from parent → local
  // ------------------------------------------------------------
  useEffect(() => {
    setOperations(value || []);
  }, [value]);

  // ------------------------------------------------------------
  // Sync local → parent
  // ------------------------------------------------------------
  const sync = (updated) => {
    setOperations(updated);
    onChange && onChange(updated);
  };

  // ------------------------------------------------------------
  // Load Process Master
  // ------------------------------------------------------------
  useEffect(() => {
    axiosClient
      .get("/api/ppe/masters/processes")
      .then((res) => setProcesses(res.data?.data || []))
      .catch(() => setProcesses([]));
  }, []);

  // ------------------------------------------------------------
  // Load Machine Groups
  // ------------------------------------------------------------
  useEffect(() => {
    axiosClient
      .get("/api/ppe/masters/machine-groups")
      .then((res) => setMachineGroups(res.data?.data || []))
      .catch(() => setMachineGroups([]));
  }, []);

  // ------------------------------------------------------------
  // Add Operation
  // ------------------------------------------------------------
  const addOperation = () => {
    if (readOnly) return;

    const updated = [
      ...operations,
      {
        seq: operations.length + 1,
        operation_name: "",
        process_id: "",
        predecessor_seq: "",
        setup_time_minutes: 0,
        run_time_minutes: 0,
        scrap_percent: 0,
        machine_groups: [],
      },
    ];
    sync(updated);
  };

  // ------------------------------------------------------------
  // Remove Operation
  // ------------------------------------------------------------
  const removeOperation = (index) => {
    if (readOnly) return;

    const updated = operations
      .filter((_, i) => i !== index)
      .map((op, idx) => ({
        ...op,
        seq: idx + 1,
      }));
    sync(updated);
  };

  // ------------------------------------------------------------
  // Handle Process Change (AUTO-FILL)
  // ------------------------------------------------------------
  const handleProcessChange = (index, processId) => {
    if (readOnly) return;

    const process = processes.find(
      (p) => p.id === Number(processId)
    );
    if (!process) return;

    const updated = [...operations];
    updated[index] = {
      ...updated[index],
      process_id: process.id,
      setup_time_minutes:
        process.setup_time_minutes || 0,
      run_time_minutes:
        process.run_time_minutes || 0,
      scrap_percent:
        process.standard_scrap_percent || 0,
    };
    sync(updated);
  };

  // ------------------------------------------------------------
  // Update Operation Field
  // ------------------------------------------------------------
  const updateField = (index, field, value) => {
    if (readOnly) return;

    const updated = [...operations];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    sync(updated);
  };

  // ------------------------------------------------------------
  // Machine Group handlers
  // ------------------------------------------------------------
  const addMachineGroup = (opIndex, mgId) => {
    if (readOnly) return;

    const updated = [...operations];
    const op = updated[opIndex];

    if (
      op.machine_groups.some(
        (m) => m.machine_group_id === mgId
      )
    )
      return;

    const nextRank = op.machine_groups.length + 1;

    op.machine_groups.push({
      machine_group_id: mgId,
      preference_rank: nextRank,
      is_primary: nextRank === 1,
      alternate_allowed: true,
    });

    sync(updated);
  };

  const updateMachineGroup = (
    opIndex,
    mgIndex,
    field,
    value
  ) => {
    if (readOnly) return;

    const updated = [...operations];
    const mgs = updated[opIndex].machine_groups;

    if (field === "is_primary" && value === true) {
      mgs.forEach((m, i) => {
        m.is_primary = i === mgIndex;
      });
    } else {
      mgs[mgIndex][field] = value;
    }

    sync(updated);
  };

  const removeMachineGroup = (opIndex, mgIndex) => {
    if (readOnly) return;

    const updated = [...operations];
    updated[opIndex].machine_groups.splice(mgIndex, 1);

    // re-rank
    updated[opIndex].machine_groups.forEach(
      (m, i) => {
        m.preference_rank = i + 1;
        if (i === 0) m.is_primary = true;
      }
    );

    sync(updated);
  };

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <strong>Routing Operations</strong>

        {!readOnly && (
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={addOperation}
          >
            + Add Operation
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <table className="table table-bordered mb-0">
          <thead className="table-light">
            <tr>
              <th>Seq</th>
              <th>Operation</th>
              <th>Process</th>
              <th>Predecessor</th>
              <th>Run</th>
              <th>Setup</th>
              <th>Scrap %</th>
              <th>Machine Groups</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {operations.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  className="text-center text-muted"
                >
                  No operations added yet
                </td>
              </tr>
            )}

            {operations.map((op, index) => (
              <tr key={index}>
                <td>{op.seq}</td>

                <td>
                  <input
                    className="form-control"
                    value={op.operation_name}
                    disabled={readOnly}
                    onChange={(e) =>
                      updateField(
                        index,
                        "operation_name",
                        e.target.value
                      )
                    }
                  />
                </td>

                <td>
                  <select
                    className="form-select"
                    value={op.process_id}
                    disabled={readOnly}
                    onChange={(e) =>
                      handleProcessChange(
                        index,
                        e.target.value
                      )
                    }
                  >
                    <option value="">
                      Select Process
                    </option>
                    {processes.map((p) => (
                      <option
                        key={p.id}
                        value={p.id}
                      >
                        {p.process_name}
                      </option>
                    ))}
                  </select>
                </td>

                <td>
                  <select
                    className="form-select"
                    value={op.predecessor_seq}
                    disabled={readOnly}
                    onChange={(e) =>
                      updateField(
                        index,
                        "predecessor_seq",
                        Number(e.target.value)
                      )
                    }
                  >
                    <option value="">
                      — None —
                    </option>
                    {operations
                      .filter(
                        (o) => o.seq < op.seq
                      )
                      .map((o) => (
                        <option
                          key={o.seq}
                          value={o.seq}
                        >
                          Seq {o.seq}
                        </option>
                      ))}
                  </select>
                </td>

                <td>
                  <input
                    type="number"
                    className="form-control"
                    value={op.run_time_minutes}
                    disabled={readOnly}
                    onChange={(e) =>
                      updateField(
                        index,
                        "run_time_minutes",
                        Number(e.target.value)
                      )
                    }
                  />
                </td>

                <td>
                  <input
                    type="number"
                    className="form-control"
                    value={op.setup_time_minutes}
                    disabled={readOnly}
                    onChange={(e) =>
                      updateField(
                        index,
                        "setup_time_minutes",
                        Number(e.target.value)
                      )
                    }
                  />
                </td>

                <td>
                  <input
                    type="number"
                    className="form-control"
                    value={op.scrap_percent}
                    disabled={readOnly}
                    onChange={(e) =>
                      updateField(
                        index,
                        "scrap_percent",
                        Number(e.target.value)
                      )
                    }
                  />
                </td>

                <td>
                  {op.machine_groups.map(
                    (mg, i) => (
                      <div
                        key={i}
                        className="border rounded p-1 mb-1"
                      >
                        <strong>
                          {
                            machineGroups.find(
                              (m) =>
                                m.id ===
                                mg.machine_group_id
                            )?.group_name
                          }
                        </strong>

                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={mg.is_primary}
                            disabled
                          />
                          <label className="form-check-label">
                            Primary
                          </label>
                        </div>

                        {!readOnly && (
                          <button
                            className="btn btn-sm btn-link text-danger"
                            onClick={() =>
                              removeMachineGroup(
                                index,
                                i
                              )
                            }
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    )
                  )}

                  {!readOnly && (
                    <select
                      className="form-select form-select-sm"
                      onChange={(e) => {
                        if (e.target.value) {
                          addMachineGroup(
                            index,
                            Number(e.target.value)
                          );
                          e.target.value = "";
                        }
                      }}
                    >
                      <option value="">
                        + Add Machine Group
                      </option>
                      {machineGroups.map(
                        (mg) => (
                          <option
                            key={mg.id}
                            value={mg.id}
                          >
                            {mg.group_name}
                          </option>
                        )
                      )}
                    </select>
                  )}
                </td>

                <td>
                  {!readOnly && (
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() =>
                        removeOperation(index)
                      }
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
